/**
 * Loads and mutates the current user's active cart. Every mutation
 * (add/update/remove/clear) re-sets `data` from the mutation's own response
 * — the backend returns the full updated cart on every call
 * (`CartResponseDto`), so there is never a second round-trip just to refresh.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  addItem as apiAddItem,
  clearCart as apiClearCart,
  getCart,
  removeItem as apiRemoveItem,
  updateItemQuantity as apiUpdateItemQuantity,
  CartRejectedError,
  CartStateChangedError,
} from './api';
import type { Cart } from './types';

const LOAD_FAILED_MESSAGE = 'We couldn’t load your cart. Please try again.';

export interface UseCart {
  cart: Cart | null;
  loading: boolean;
  /** Set while an add/update/remove/clear mutation is in flight — disable controls, not the whole page. */
  mutating: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addItem: (collectionCode: string, quantity?: number) => Promise<boolean>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<boolean>;
  removeItem: (cartItemId: string) => Promise<boolean>;
  clear: () => Promise<boolean>;
}

export function useCart(): UseCart {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCart(await getCart());
    } catch {
      setError(LOAD_FAILED_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const runMutation = useCallback(
    async (fn: () => Promise<Cart>): Promise<boolean> => {
      setMutating(true);
      setError(null);
      try {
        setCart(await fn());
        return true;
      } catch (err) {
        if (err instanceof CartRejectedError) {
          setError(err.message);
        } else if (err instanceof CartStateChangedError) {
          setError(err.message);
          // The cart moved (converted/expired/concurrent edit elsewhere) —
          // re-fetch so the UI reflects reality instead of stale local state.
          void reload();
        } else {
          setError('Something went wrong. Please try again.');
        }
        return false;
      } finally {
        setMutating(false);
      }
    },
    [reload],
  );

  const addItem = useCallback(
    (collectionCode: string, quantity = 1) => runMutation(() => apiAddItem({ collectionCode, quantity })),
    [runMutation],
  );
  const updateQuantity = useCallback(
    (cartItemId: string, quantity: number) => runMutation(() => apiUpdateItemQuantity(cartItemId, quantity)),
    [runMutation],
  );
  const removeItem = useCallback(
    (cartItemId: string) => runMutation(() => apiRemoveItem(cartItemId)),
    [runMutation],
  );
  const clear = useCallback(() => runMutation(() => apiClearCart()), [runMutation]);

  return { cart, loading, mutating, error, reload, addItem, updateQuantity, removeItem, clear };
}
